import { memo, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDetectClickOutside } from 'hooks/useDetectClickOutside'
import { setIsOpened } from 'store/widget'
import { messagesActions, messagesSelectors } from 'store/messages'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { settingsActions, settingsSelectors } from 'store/settings'
import { Icon } from 'components/ui/Icon'
import _ from 'lodash'
import { useModalActions } from 'providers/ModalProvider'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { useIsAuthModeExternal, useIsAuthModeNoLogin } from 'hooks/useAuthMode'
import { useGetSetting } from 'api/settings'
import { AppVersionBanner } from 'components/app-version/AppVersionBanner'
import { useLogout } from 'hooks/useLogout'

const WidgetHeaderComponent = () => {
  const dispatch = useDispatch()
  const logout = useLogout()
  const { setting, title, description } = useGetSetting()
  const { t } = useTranslation()
  const chatId = useSelector(messagesSelectors.chatId)
  const isLogged = useSelector(settingsSelectors.isLogged)
  const showExpandButton = setting?.showWidgetExpandButton === true
  const { isAppMode, isWebMode } = useGetBotMode()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useDetectClickOutside({
    setShowModal: setShowDropdown,
  })
  const { showLogin, showExportChat } = useModalActions()

  const isExternal = useIsAuthModeExternal()
  const isNoLogin = useIsAuthModeNoLogin()

  const dropdownMenu = useMemo(() => {
    return _.compact([
      isLogged &&
        !isExternal && {
          title: t('logout'),
          callback: logout,
          icon: <Icon icon={['far', 'sign-out-alt']} className="tw-h-4 tw-w-4" />,
        },
      !isLogged &&
        !isExternal &&
        !isNoLogin && {
          title: t('login'),
          callback: () => showLogin(),
          icon: <Icon icon={['far', 'sign-in-alt']} className="tw-h-4 tw-w-4" />,
        },
      isWebMode && {
        title: t('close'),
        icon: <Icon icon={['far', 'times']} className="tw-h-4 tw-w-4" />,
        callback: () => {
          dispatch(setIsOpened(false))
          setShowDropdown(false)
        },
      },
      showExpandButton && {
        title: t('expand'),
        icon: <Icon icon={['far', 'expand']} className="tw-h-4 tw-w-4" />,
        callback: () => {
          window.parent.postMessage({ type: 'expandWidget' }, '*')
          setShowDropdown(false)
        },
      },
      !!chatId && {
        title: t('goBack'),
        icon: <Icon icon={['far', 'arrow-left']} className="tw-h-4 tw-w-4" />,
        callback: () => {
          dispatch(messagesActions.setChatId(undefined))
          dispatch(settingsActions.setTicketReminderVisible(false))
          setShowDropdown(false)
        },
      },
      isLogged &&
        !!chatId && {
          title: t('exportChat'),
          callback: () => {
            setShowDropdown(false)
            showExportChat()
          },
          icon: <Icon icon={['far', 'file-export']} className="tw-h-4 tw-w-4" />,
        },
    ])
  }, [
    t,
    chatId,
    isLogged,
    isWebMode,
    isExternal,
    isNoLogin,
    showExpandButton,
    showLogin,
    showExportChat,
    dispatch,
    logout,
  ])

  return (
    <>
      <div
        className={clsx(
          'tw-relative tw-flex tw-cursor-default tw-items-center tw-gap-2 tw-p-[16px]',
          isAppMode ? '' : 'tw-rounded-t-[16px]',
        )}
        style={{
          background: 'var(--header-gradient)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          overflow: 'hidden',
          height: '72px',
          minHeight: '72px',
          maxHeight: '72px',
        }}
      >
        {setting?.logo?.full && (
          <div className="tw-shrink-0 tw-p-1">
            <img
              className="tw-h-10 tw-w-10 tw-rounded-md"
              src={setting?.logo?.thumbnail?.medium ?? setting?.logo}
              alt="Bot Logo"
            />
          </div>
        )}
        <div className="tw-flex-1">
          <div className="tw-text-base tw-font-bold tw-leading-6" style={{ color: 'var(--text-primary)' }}>
            {title}
          </div>
          <p className="tw-text-sm tw-leading-6" style={{ color: 'var(--text-secondary)' }}>
            {description}
          </p>
          <AppVersionBanner />
        </div>
        <button
          type="button"
          aria-label={t('menu')}
          aria-haspopup="menu"
          aria-expanded={showDropdown}
          className="tw-flex hover-scale tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-2 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <Icon icon={['far', 'bars']} size={'1x'} style={{ color: 'var(--icon-default)' }} />
        </button>
      </div>
      {showDropdown && (
        <div
          id="dropdown"
          className="tw-absolute tw-right-3 tw-top-14 tw-z-50 tw-w-fit tw-cursor-default tw-divide-y tw-divide-faint tw-rounded-xl tw-bg-surface tw-shadow-lg"
          ref={dropdownRef}
        >
          <div
            role="menu"
            className="tw-rounded-lg tw-py-1 tw-text-sm"
            style={{
              backgroundColor: 'var(--surface-solid)',
              color: 'var(--icon-default)',
              border: '1px solid var(--border-default)',
            }}
          >
            {dropdownMenu.map((item, idx) => {
              return (
                <button
                  type="button"
                  role="menuitem"
                  key={idx}
                  className="tw-hover:opacity-70 tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-text-left focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:-tw-outline-offset-2"
                  onClick={item.callback}
                >
                  <div className="tw-flex tw-items-center tw-justify-center tw-pl-2">{item.icon}</div>
                  <span className="tw-block tw-px-2 tw-py-2">{item.title}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export const WidgetHeader = memo(WidgetHeaderComponent)
